import type {
  ChartSeries,
  DiskSnapshot,
  Sample,
  SystemSnapshot,
  SystemStatsResponse,
} from './types';

/**
 * Fall back to the processor's own loop period when the response is too short
 * to measure one.
 */
export const DEFAULT_SAMPLE_INTERVAL = 10;

interface SeriesMeta {
  id: string;
  label: string;
  color: string;
}

function finiteOr(value: unknown, fallback: number): number {
  return typeof value === 'number' && Number.isFinite(value) ? value : fallback;
}

function normalizeDisks(raw: unknown): Record<string, DiskSnapshot> {
  const disks: Record<string, DiskSnapshot> = {};

  if (!raw || typeof raw !== 'object') return disks;

  Object.entries(raw as Record<string, unknown>).forEach(([key, value]) => {
    if (!value || typeof value !== 'object') return;

    const disk = value as Record<string, unknown>;

    disks[key] = {
      usage: finiteOr(disk.usage, 0),
      bytes_read: finiteOr(disk.bytes_read, 0),
      bytes_written: finiteOr(disk.bytes_written, 0),
    };
  });

  return disks;
}

function normalizeSnapshot(raw: Record<string, unknown>, start: number): SystemSnapshot {
  const network = (raw.network ?? {}) as Record<string, unknown>;
  const swap = raw.swap_usage;

  return {
    cpu_usage: finiteOr(raw.cpu_usage, 0),
    mem_usage: finiteOr(raw.mem_usage, 0),
    // Deliberately not coerced to 0: null means "this host has no swap", which
    // the panel reports differently from swap sitting idle at zero.
    swap_usage: typeof swap === 'number' && Number.isFinite(swap) ? swap : null,
    disks: normalizeDisks(raw.disks),
    network: {
      received_bytes: finiteOr(network.received_bytes, 0),
      sent_bytes: finiteOr(network.sent_bytes, 0),
    },
    // A processor predating these two fields sends neither, and an entry from
    // one was exactly a single raw sample ending at its own key.
    end_time: Math.max(finiteOr(raw.end_time, start), start),
    // Never below 1: it is a divisor.
    sample_count: Math.max(1, Math.round(finiteOr(raw.sample_count, 1))),
  };
}

/**
 * Turn the response map into an ascending list of samples.
 *
 * A malformed entry is skipped rather than thrown on. The map holds one entry
 * per ten seconds of server history, and losing the whole chart because one of
 * a hundred entries is odd would be a bad trade.
 */
export function toSamples(response: SystemStatsResponse): Sample[] {
  const samples: Sample[] = [];

  Object.keys(response ?? {}).forEach((key) => {
    const start = Number(key);

    if (!Number.isInteger(start) || start <= 0) return;

    const raw = response[key];

    if (!raw || typeof raw !== 'object') return;

    samples.push({
      start,
      snapshot: normalizeSnapshot(raw as unknown as Record<string, unknown>, start),
    });
  });

  // Rust HashMap iteration order is arbitrary and varies between requests, so
  // the order the keys arrive in carries no meaning at all.
  samples.sort((left, right) => left.start - right.start);

  return samples;
}

/**
 * How many seconds one raw sample covers.
 *
 * The byte counters are sums over an entry and the processor's refresh period
 * appears nowhere in the payload, so it has to be measured. Only gaps between
 * two consecutive *raw* samples measure it - a gap touching a downsampled
 * bucket spans however many periods that bucket swallowed.
 *
 * The median, not the mean, so one stalled interval cannot drag the estimate.
 */
export function estimateSampleInterval(samples: Sample[]): number {
  const gaps: number[] = [];

  for (let i = 1; i < samples.length; i += 1) {
    if (samples[i].snapshot.sample_count !== 1) continue;
    if (samples[i - 1].snapshot.sample_count !== 1) continue;

    const gap = samples[i].start - samples[i - 1].start;

    if (gap > 0) gaps.push(gap);
  }

  if (gaps.length === 0) return DEFAULT_SAMPLE_INTERVAL;

  gaps.sort((left, right) => left - right);

  return gaps[Math.floor(gaps.length / 2)];
}

/**
 * The x position of each sample.
 *
 * `end_time`, not the map key: a value describes the interval *ending* there,
 * which holds for a raw sample covering the preceding refresh and for a bucket
 * whose average ends at its newest member.
 */
export function chartTimestamps(samples: Sample[]): number[] {
  return samples.map((sample) => sample.snapshot.end_time);
}

export function chartSampleCounts(samples: Sample[]): number[] {
  return samples.map((sample) => sample.snapshot.sample_count);
}

/** A 0..1 ratio rendered on a 0-100 axis. */
export function percentSeries(
  samples: Sample[],
  pick: (snapshot: SystemSnapshot) => number | null,
  meta: SeriesMeta,
): ChartSeries {
  return {
    ...meta,
    points: samples.map((sample) => {
      const value = pick(sample.snapshot);

      return value === null ? null : value * 100;
    }),
  };
}

/**
 * A summed byte counter rendered as bytes per second.
 *
 * The divisor is `sample_count * interval` - the wall time the entry actually
 * covers. Plotting the raw sums instead makes an old, wide bucket look like a
 * traffic spike purely because it swallowed more samples.
 */
export function rateSeries(
  samples: Sample[],
  pick: (snapshot: SystemSnapshot) => number | null,
  intervalSeconds: number,
  meta: SeriesMeta,
): ChartSeries {
  return {
    ...meta,
    points: samples.map((sample) => {
      const bytes = pick(sample.snapshot);

      if (bytes === null) return null;

      const seconds = sample.snapshot.sample_count * intervalSeconds;

      return seconds > 0 ? bytes / seconds : null;
    }),
  };
}

/**
 * The same thing as `rateSeries`, converted to megabits per second.
 *
 * Network links are rated in bits - a "1 Gbps" NIC - so an admin checking a
 * reading against what the interface is supposed to do needs bits, not bytes.
 * The conversion happens here rather than in the formatter so that the chart's
 * auto-scaled axis lands on round Mbps values too.
 */
export function mbpsSeries(
  samples: Sample[],
  pick: (snapshot: SystemSnapshot) => number | null,
  intervalSeconds: number,
  meta: SeriesMeta,
): ChartSeries {
  const rate = rateSeries(samples, pick, intervalSeconds, meta);

  return {
    ...rate,
    points: rate.points.map((point) => (point === null ? null : (point * 8) / 1_000_000)),
  };
}

/**
 * Every disk seen anywhere in the response, sorted.
 *
 * The union rather than the newest entry's keys: a volume that unmounts partway
 * through the window keeps its block with a gap in the line, instead of the tab
 * reflowing under the reader when it disappears. Merged buckets union their
 * members' disks too, so old buckets carry volumes the newest entries do not.
 */
export function diskKeys(samples: Sample[]): string[] {
  const keys = new Set<string>();

  samples.forEach((sample) => {
    Object.keys(sample.snapshot.disks).forEach((key) => keys.add(key));
  });

  return [...keys].sort((left, right) => left.localeCompare(right));
}

/**
 * The processor builds its disk keys by Debug-formatting a Path and an OsStr,
 * so the raw key contains literal quote characters. The key stays the identity;
 * this is only the display form, and anything that does not match is shown
 * verbatim - Windows paths and unusual device names among them.
 */
const DISK_KEY_PATTERN = /^"(.*)" \("(.*)"\)$/;

export function labelDisk(key: string): string {
  const match = DISK_KEY_PATTERN.exec(key);

  return match ? `${match[1]} (${match[2]})` : key;
}

/** False when every sample says null - i.e. the host has no swap at all. */
export function hasSwapData(samples: Sample[]): boolean {
  return samples.some((sample) => sample.snapshot.swap_usage !== null);
}

/** True once any entry has been downsampled, which the charts flag to the reader. */
export function hasAggregatedSamples(samples: Sample[]): boolean {
  return samples.some((sample) => sample.snapshot.sample_count > 1);
}
